import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, addDoc, getDocs, query, where, deleteDoc, doc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useLang } from '../LanguageContext';

const INTERVALLE_MS = 10 * 60 * 1000; // 10 minutes
const TILE_SATELLITE = 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}';
const TILE_LABELS = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png';

// ─── HOOK TRACKING (utilisé dans CommercialHome) ─────────────────────────────
export function useTracking(user) {
  const intervalRef = useRef(null);
  const [actif, setActif] = useState(false);
  const [erreur, setErreur] = useState('');
  const [dernierPoint, setDernierPoint] = useState(null);

  const enregistrerPosition = useCallback(async () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await addDoc(collection(db, 'tracking'), {
            commercialId: user.uid,
            commercialNom: `${user.prenom || ''} ${user.nom || ''}`.trim(),
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            precision: Math.round(pos.coords.accuracy),
            timestamp: serverTimestamp(),
            date: new Date().toISOString().split('T')[0],
          });
          setDernierPoint({ lat: pos.coords.latitude, lng: pos.coords.longitude, heure: new Date() });
          setErreur('');
        } catch (e) { setErreur('Erreur Firebase: ' + e.message); }
      },
      (err) => { setErreur('GPS indisponible: ' + err.message); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, [user]);

  const demarrer = useCallback(async () => {
    if (!navigator.geolocation) { setErreur('GPS non supporté'); return; }
    setActif(true);
    await enregistrerPosition(); // Premier point immédiat
    intervalRef.current = setInterval(enregistrerPosition, INTERVALLE_MS);
  }, [enregistrerPosition]);

  const arreter = useCallback(() => {
    setActif(false);
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
  }, []);

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  // Auto-démarrer si le commercial active le tracking
  useEffect(() => {
    const saved = localStorage.getItem(`kdp_tracking_${user.uid}`);
    if (saved === 'on') demarrer();
  }, []);

  const toggleTracking = async () => {
    if (actif) {
      arreter();
      localStorage.setItem(`kdp_tracking_${user.uid}`, 'off');
    } else {
      await demarrer();
      localStorage.setItem(`kdp_tracking_${user.uid}`, 'on');
    }
  };

  return { actif, erreur, dernierPoint, toggleTracking };
}

// ─── MINI WIDGET TRACKING (dans CommercialHome) ───────────────────────────────
export function TrackingWidget({ user }) {
  const { actif, erreur, dernierPoint, toggleTracking } = useTracking(user);

  return (
    <div style={{
      background: actif ? '#EAF3DE' : '#f5f5f5',
      border: `1px solid ${actif ? '#b6d99b' : '#ddd'}`,
      borderRadius: 10, padding: '10px 14px',
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <div style={{ fontSize: 20 }}>{actif ? '📡' : '📍'}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: actif ? '#3B6D11' : '#555' }}>
          {actif ? 'Tracking GPS actif' : 'Tracking GPS inactif'}
        </div>
        {actif && dernierPoint && (
          <div style={{ fontSize: 11, color: '#3B6D11', marginTop: 2 }}>
            Dernier point : {dernierPoint.heure.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
        {erreur && <div style={{ fontSize: 11, color: '#C0392B', marginTop: 2 }}>{erreur}</div>}
        {!actif && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Position enregistrée toutes les 10 min</div>}
      </div>
      <button
        onClick={toggleTracking}
        style={{
          background: actif ? '#C0392B' : '#3B6D11',
          border: 'none', borderRadius: 8,
          color: '#fff', fontSize: 12, fontWeight: 600,
          padding: '7px 12px', cursor: 'pointer', flexShrink: 0,
        }}
      >
        {actif ? '⏹ Arrêter' : '▶ Démarrer'}
      </button>
    </div>
  );
}

// ─── PAGE HISTORIQUE TRACKING (Manager) ──────────────────────────────────────
export default function TrackingGPS({ onClose }) {
  const [commerciaux, setCommerciaux] = useState([]);
  const [commSelectionne, setCommSelectionne] = useState('');
  const [dateSelectionnee, setDateSelectionnee] = useState(new Date().toISOString().split('T')[0]);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listeVisible, setListeVisible] = useState(false);

  const [pointActif, setPointActif] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const polylineRef = useRef(null);

  useEffect(() => {
    chargerCommerciaux();
    chargerLeaflet();
  }, []);

  const chargerLeaflet = () => {
    if (window.L) { setMapLoaded(true); return; }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => setMapLoaded(true);
    document.head.appendChild(script);
  };

  const chargerCommerciaux = async () => {
    const snap = await getDocs(query(collection(db, 'users'), where('role', '==', 'commercial')));
    setCommerciaux(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const chargerPoints = async () => {
    if (!commSelectionne || !dateSelectionnee) return;
    setLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'tracking'),
        where('commercialId', '==', commSelectionne),
        where('date', '==', dateSelectionnee)
      ));
      const sorted = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const ta = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(0);
          const tb = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(0);
          return ta - tb;
        });
      setPoints(sorted);
    } catch (e) { console.log(e); }
    setLoading(false);
  };

  useEffect(() => { if (commSelectionne && dateSelectionnee) chargerPoints(); }, [commSelectionne, dateSelectionnee]);

  // Initialiser la carte
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || mapInstanceRef.current) return;
    const map = window.L.map(mapRef.current, { zoomControl: true }).setView([36.35, 6.61], 11);
    window.L.tileLayer(TILE_SATELLITE, { attribution: '© Esri', maxZoom: 20 }).addTo(map);
    window.L.tileLayer(TILE_LABELS, { attribution: '© CARTO', maxZoom: 20, subdomains: 'abcd', opacity: 0.9 }).addTo(map);
    mapInstanceRef.current = map;
  }, [mapLoaded]);

  // Afficher les points sur la carte
  useEffect(() => {
    if (!mapInstanceRef.current || !window.L) return;
    const map = mapInstanceRef.current;

    // Nettoyer anciens marqueurs
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];
    if (polylineRef.current) { map.removeLayer(polylineRef.current); polylineRef.current = null; }

    if (points.length === 0) return;

    const latlngs = points.map(p => [p.lat, p.lng]);

    // Tracé du trajet
    polylineRef.current = window.L.polyline(latlngs, {
      color: '#C0392B', weight: 3, opacity: 0.8, dashArray: '6, 4'
    }).addTo(map);

    // Marqueurs
    points.forEach((p, i) => {
      const heure = p.timestamp?.toDate ? p.timestamp.toDate().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '--:--';
      const isFirst = i === 0;
      const isLast = i === points.length - 1;

      const color = isFirst ? '#3B6D11' : isLast ? '#C0392B' : '#185FA5';
      const icon = window.L.divIcon({
        html: `<div style="
          background:${color};color:#fff;border-radius:50%;
          width:${isFirst || isLast ? 28 : 18}px;
          height:${isFirst || isLast ? 28 : 18}px;
          display:flex;align-items:center;justify-content:center;
          font-size:${isFirst || isLast ? 12 : 9}px;font-weight:700;
          border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);
        ">${isFirst ? '▶' : isLast ? '⏹' : i + 1}</div>`,
        className: '', iconSize: [isFirst || isLast ? 28 : 18, isFirst || isLast ? 28 : 18],
        iconAnchor: [isFirst || isLast ? 14 : 9, isFirst || isLast ? 14 : 9],
      });

      const marker = window.L.marker([p.lat, p.lng], { icon })
        .addTo(map)
        .on('click', () => setPointActif(i))
        .bindPopup(`
          <div style="font-family:system-ui;min-width:140px">
            <div style="font-weight:700;color:#1C2833;margin-bottom:4px">
              ${isFirst ? '🟢 Départ' : isLast ? '🔴 Fin' : `Point ${i + 1}`}
            </div>
            <div style="font-size:12px;color:#555">🕐 ${heure}</div>
            <div style="font-size:11px;color:#888;margin-top:2px">
              📍 ${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}
            </div>
            ${p.precision ? `<div style="font-size:11px;color:#888">🎯 Précision : ${p.precision}m</div>` : ''}
          </div>
        `);
      markersRef.current.push(marker);
    });

    // Centrer la carte
    map.fitBounds(polylineRef.current.getBounds(), { padding: [30, 30] });
  }, [points]);

  const commInfo = commerciaux.find(c => c.id === commSelectionne);
  const dureeTrajet = points.length >= 2 ? (() => {
    const debut = points[0].timestamp?.toDate?.();
    const fin = points[points.length - 1].timestamp?.toDate?.();
    if (!debut || !fin) return null;
    const diff = Math.round((fin - debut) / 60000);
    return `${Math.floor(diff / 60)}h${String(diff % 60).padStart(2, '0')}`;
  })() : null;

  return (
    <div style={styles.app}>
      <div style={styles.topbar}>
        <button onClick={onClose} style={styles.backBtn}>←</button>
        <div style={{ flex: 1 }}>
          <div style={styles.topbarTitle}>📡 Tracking GPS</div>
          <div style={styles.topbarSub}>Historique des déplacements</div>
        </div>
      </div>

      {/* FILTRES */}
      <div style={styles.filtreBar}>
        <select style={styles.select} value={commSelectionne} onChange={e => setCommSelectionne(e.target.value)}>
          <option value="">-- Choisir un commercial --</option>
          {commerciaux.map(c => <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>)}
        </select>
<input type="date" style={styles.dateInput} value={dateSelectionnee}
          max={new Date().toISOString().split('T')[0]}
          onChange={e => setDateSelectionnee(e.target.value)} />
      </div>

      {/* STATS */}
      {points.length > 0 && (
        <div style={styles.statsBar}>
          <div style={styles.statPill}>📍 {points.length} points</div>
          {dureeTrajet && <div style={styles.statPill}>⏱ {dureeTrajet}</div>}
          <div style={styles.statPill}>📅 {new Date(dateSelectionnee).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
          {commInfo && <div style={styles.statPill}>👤 {commInfo.prenom} {commInfo.nom}</div>}
        </div>
      )}

      {/* CARTE */}
      <div style={{ flex: 1, position: 'relative', minHeight: 300 }}>
        {!commSelectionne && (
          <div style={styles.placeholder}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📡</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1C2833' }}>Tracking GPS</div>
            <div style={{ fontSize: 13, color: '#888', marginTop: 6, textAlign: 'center', maxWidth: 240 }}>
              Sélectionnez un commercial et une date pour voir son trajet
            </div>
          </div>
        )}
        {commSelectionne && points.length === 0 && !loading && (
          <div style={styles.placeholder}>
            <div style={{ fontSize: 40 }}>🗺️</div>
            <div style={{ fontSize: 14, color: '#888', marginTop: 10, textAlign: 'center' }}>
              Aucun point GPS pour cette journée
            </div>
            <div style={{ fontSize: 12, color: '#aaa', marginTop: 6, textAlign: 'center', maxWidth: 240 }}>
              Le commercial doit activer le tracking dans l'app
            </div>
          </div>
        )}
        {loading && (
          <div style={styles.placeholder}>
            <div style={{ fontSize: 14, color: '#888' }}>Chargement...</div>
          </div>
        )}
        <div ref={mapRef} style={{ width: '100%', height: '75vh', minHeight: 500 }} />
      </div>

      {/* BOUTON LISTE + LISTE POINTS */}
      {points.length > 0 && (
        <div style={styles.listePoints}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '10px 14px 6px', borderBottom: listeVisible ? '1px solid #eee' : 'none' }}>
            <div style={styles.listeTitle}>Chronologie du trajet</div>
            <button
              onClick={() => setListeVisible(v => !v)}
              style={{ marginLeft: 'auto', background: '#1C2833', color: '#fff', border: 'none', borderRadius: 8, padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}
            >
              {listeVisible ? '🗺️ Masquer' : '📋 Afficher liste'}
            </button>
          </div>
          {listeVisible && (
            <div style={styles.listeScroll}>
              {points.map((p, i) => {
                const heure = p.timestamp?.toDate ? p.timestamp.toDate().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '--:--';
                const isFirst = i === 0;
                const isLast = i === points.length - 1;
                const isActif = pointActif === i;
                return (
                  <div key={p.id}
                    style={{ ...styles.pointRow, background: isActif ? '#EAF3DE' : '#fff', cursor: 'pointer' }}
                    onClick={() => {
                      setPointActif(i);
                      if (mapInstanceRef.current) {
                        mapInstanceRef.current.setView([p.lat, p.lng], 16);
                        markersRef.current[i]?.openPopup();
                      }
                    }}>
                    <div style={{ ...styles.pointBadge, background: isFirst ? '#3B6D11' : isLast ? '#C0392B' : '#185FA5' }}>
                      {isFirst ? '▶' : isLast ? '⏹' : i + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1C2833' }}>
                        {isFirst ? 'Départ' : isLast ? 'Fin de journée' : `Point ${i + 1}`}
                      </div>
                      <div style={{ fontSize: 11, color: '#888' }}>
                        {heure} · {p.lat.toFixed(4)}, {p.lng.toFixed(4)}
                        {p.precision ? ` · ±${p.precision}m` : ''}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: '#185FA5' }}>👁</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  app: { maxWidth: 480, margin: '0 auto', minHeight: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' },
  topbar: { background: '#1C2833', padding: '1rem 1.25rem', color: '#fff', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  backBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', fontSize: 16, cursor: 'pointer', flexShrink: 0 },
  topbarTitle: { fontSize: 15, fontWeight: 600 },
  topbarSub: { fontSize: 11, opacity: 0.7, marginTop: 2 },
  filtreBar: { background: '#fff', padding: '10px 12px', borderBottom: '1px solid #eee', display: 'flex', gap: 8, flexShrink: 0 },
  select: { flex: 1, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, background: '#f9f9f9' },
  dateInput: { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, background: '#f9f9f9' },
  statsBar: { background: '#fff', padding: '8px 12px', borderBottom: '1px solid #eee', display: 'flex', gap: 6, flexWrap: 'wrap', flexShrink: 0 },
  statPill: { fontSize: 11, background: '#f0f0f0', borderRadius: 20, padding: '3px 10px', color: '#555' },
  placeholder: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', zIndex: 10 },
  listePoints: { background: '#fff', borderTop: '1px solid #eee', flexShrink: 0, maxHeight: 220, display: 'flex', flexDirection: 'column' },
  listeTitle: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, padding: '10px 14px 6px' },
  listeScroll: { overflowY: 'auto', flex: 1 },
  pointRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderBottom: '1px solid #f5f5f5' },
  pointBadge: { width: 24, height: 24, borderRadius: '50%', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
};
